import {action, makeObservable, observable, runInAction} from "mobx";
import {v4 as uuidv4} from "uuid";

import {AdminApi} from "../../api/api";
import {PageStoreConstructorOptions} from "../../utils/page-store";
import {PageRouter} from "../../utils/route-util";
import {validateLength} from "../../utils/validation/functions/length";
// MEMO:
// 以下の行はコメントアウトされていますが、将来的に使用する可能性があります.
// import {validateSlug} from "../../utils/validation/functions/slug";
import {validateLengthWithSlug} from "../../utils/validation/functions/slug";
import {ValidationResultItem} from "../../utils/validation/validation-result-item";
import {Stores} from "..";

export type StringType = "STRING";
export type ImageType = "IMAGE";
export const STRING_TYPE: StringType = "STRING";
export const IMAGE_TYPE: ImageType = "IMAGE";

export type DataType = StringType | ImageType;
export type TableRow = {
    id: string;
    slug: string;
    slugError: string;
    label: string;
    labelError: string;
    note: string;
    noteError: string;
    type: DataType;
    deleted: boolean;
};

export abstract class DatabaseFormBaseStore {
    /**
     * Observables
     */
    @observable public mediaId = "";
    @observable public templateName = "";
    @observable public templateNameError = "";
    @observable public templateNote = "";
    @observable public templateNoteError = "";
    @observable public templateColumnList: TableRow[] = [];

    protected adminApi: AdminApi;
    protected stores: Stores;
    protected pageRouter: PageRouter;

    /**
     * Constructor
     */
    constructor(protected options: PageStoreConstructorOptions) {
        makeObservable(this);
        this.adminApi = options.adminApi;
        this.stores = options.stores;
        this.pageRouter = options.pageRouter;
        runInAction(() => {
            this.templateColumnList = [this.createEmptyRow()];
        });
    }

    /**
     *   メディアIDを設定する
     *   @param {string} mediaId - メディアID
     */
    @action.bound
    public setMediaId(mediaId: string) {
        this.mediaId = mediaId;
    }

    /**
     *   テンプレート名を設定する
     *   @param {string} newName - 新しいテンプレート名
     */
    @action.bound
    public async setTemplateName(newName: string) {
        this.templateName = newName;
        if (!newName.trim()) {
            this.templateNameError = "テンプレート名は必須です。";
            return;
        }
        const errs: ValidationResultItem[] = await Promise.resolve(
            validateLength(1, 16)(newName.trim()),
        );
        runInAction(() => {
            this.templateNameError = errs.length ? errs[0].errorMessage : "";
        });
    }

    /**
     *   テンプレートの説明を設定する
     *   @param {string} newNote - 新しい説明
     */
    @action.bound
    public async setTemplateNote(newNote: string) {
        this.templateNote = newNote;
        const errs: ValidationResultItem[] = await Promise.resolve(
            validateLength(0, 512)(newNote.trim()),
        );
        runInAction(() => {
            this.templateNoteError = errs.length ? errs[0].errorMessage : "";
        });
    }

    /**
     *   テンプレート行を追加する
     *   @return {void}
     */
    @action.bound
    public addTemplateRow(): void {
        this.templateColumnList.push(this.createEmptyRow());
    }

    /**
     *   行を削除する
     *   @param {string} id - 行のID
     *   @return {void}
     */
    @action.bound
    public removeTemplateRow(id: string): void {
        this.templateColumnList = this.templateColumnList.filter((r) => r.id !== id);
    }

    /**
     *   スラッグを設定する
     *   @param {string} id - 行のID
     *   @param {string} newSlug - 新しいスラッグ
     */
    @action.bound
    public async setSlug(id: string, newSlug: string) {
        const row = this.templateColumnList.find((r) => r.id === id);
        if (!row) return;
        row.slug = newSlug;
        const trimmed = newSlug.trim();

        // MEMO:
        // スラッグバリデーションを文字列超過と文字種チェックに分割する場合、以下のコメントアウトを解除してください。
        // const [lenErrs, slugErrs] = await Promise.all([
        const [lenErrs] = await Promise.all([
            validateLengthWithSlug(1, 16)(trimmed),
            // MEMO:
            // スラッグバリデーションを文字列超過と文字種チェックに分割する場合、以下のコメントアウトを解除してください。
            // validateLength(1, 16)(trimmed),
            // validateSlug()(trimmed),
        ]);
        runInAction(() => {
            if (!trimmed) {
                row.slugError = "項目IDは必須です。";
                // MEMO:
                // スラッグバリデーションを文字列超過と文字種チェックに分割する場合、以下のコメントアウトを解除してください。
                // } else if (slugErrs.length) {
                //     row.slugError = slugErrs[0].errorMessage;
            } else if (lenErrs.length) {
                row.slugError = lenErrs[0].errorMessage;
            } else {
                const counts = new Map<string, number>();
                this.templateColumnList.forEach((r2) => {
                    const s = r2.slug.trim();
                    if (s) counts.set(s, (counts.get(s) ?? 0) + 1);
                });
                row.slugError = counts.get(trimmed)! > 1 ? "項目IDが重複しています。" : "";
            }
        });
    }

    /**
     *   ラベルを設定する
     *   @param {string} id - 行のID
     *   @param {string} newLabel - 新しいラベル
     */
    @action.bound
    public async setLabel(id: string, newLabel: string) {
        const row = this.templateColumnList.find((r) => r.id === id);
        if (!row) return;
        row.label = newLabel;
        if (!newLabel.trim()) {
            row.labelError = "項目名は必須です。";
            return;
        }
        const errs: ValidationResultItem[] = await Promise.resolve(
            validateLength(1, 16)(newLabel.trim()),
        );
        runInAction(() => {
            row.labelError = errs.length ? errs[0].errorMessage : "";
        });
    }

    /**
     *   説明を設定する
     *   @param {string} id - 行のID
     *   @param {string} newNote - 新しい説明
     */
    @action.bound
    public async setNote(id: string, newNote: string, isComposing = false) {
        const row = this.templateColumnList.find((r) => r.id === id);
        if (!row) return;

        // 入力中はリアルタイムで文字を上書き
        row.note = newNote;

        // IME変換中はバリデーションをスキップ
        if (isComposing) {
            return;
        }

        const trimmed = newNote.trim();
        const errs: ValidationResultItem[] = await Promise.resolve(validateLength(0, 512)(trimmed));
        runInAction(() => {
            row.noteError = errs.length ? errs[0].errorMessage : "";
        });
    }

    /**
     *   データ型を設定する
     *   @param {string} id - 行のID
     *   @param {DataType} newType - 新しいデータ型
     */
    @action.bound
    public setDataTypeSelect(id: string, newType: DataType) {
        const row = this.templateColumnList.find((r) => r.id === id);
        if (!row) return;
        row.type = newType;
    }

    /**
     *   空の行を作成する
     *   @return {TableRow} 空の行
     */

    protected createEmptyRow(): TableRow {
        return {
            id: uuidv4(),
            slug: "",
            slugError: "",
            label: "",
            labelError: "",
            note: "",
            noteError: "",
            type: STRING_TYPE,
            deleted: false,
        };
    }
    /**
     *   保存可能かどうかを判定する
     *   @return {boolean} 保存可能ならtrue、そうでなければfalse
     */
    public canSave(): boolean {
        if (this.templateNameError || !this.templateName.trim()) return false;
        if (this.templateNoteError) return false;
        for (const row of this.templateColumnList) {
            if (row.slugError || row.labelError || row.noteError) return false;
            if (!row.slug.trim() || !row.label.trim()) return false;
        }
        return true;
    }
}
